import {
  collection,
  addDoc,
  query,
  orderBy,
  where,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import { db2026 } from "../config/firebase";
import { getActiveDb } from "../config/tournamentRuntime";

const getDb = () => getActiveDb() || db2026;

// Collection name for audit logs
const AUDIT_COLLECTION = "audit_logs";

export const auditService = {
  // Log a score change event
  async logScoreChange(action, scoreData, userEmail, oldScore = null) {
    try {
      const auditEntry = {
        // Action details
        action: action, // "CREATE", "UPDATE", "DELETE"
        timestamp: new Date(),

        // User details
        userEmail: userEmail,
        userId: scoreData.updatedBy || userEmail,

        // Score details
        teamId: scoreData.teamId,
        hole: scoreData.hole,
        round: scoreData.round,
        newScore: scoreData.strokes,
        oldScore: oldScore,
        scoreId: scoreData.id,

        // Additional context
        ipAddress: null, // Could be added with additional setup
        userAgent: navigator.userAgent,
        sessionId: sessionStorage.getItem("sessionId") || "unknown",

        // Metadata
        auditId: `${scoreData.teamId}-${scoreData.hole}-${
          scoreData.round
        }-${Date.now()}`,
        version: "1.0",
      };

      const auditRef = await addDoc(
        collection(getDb(), AUDIT_COLLECTION),
        auditEntry
      );

      console.log("✅ Audit log created:", auditRef.id);
      return auditRef.id;
    } catch (error) {
      console.error("❌ Error creating audit log:", error);
      // Don't throw error - audit logging shouldn't break score submission
      return null;
    }
  },

  // Log score creation
  async logScoreCreate(scoreData, userEmail) {
    return this.logScoreChange("CREATE", scoreData, userEmail);
  },

  // Log score update
  async logScoreUpdate(scoreData, userEmail, oldScore) {
    return this.logScoreChange("UPDATE", scoreData, userEmail, oldScore);
  },

  // Log score deletion (if needed)
  async logScoreDelete(scoreData, userEmail) {
    return this.logScoreChange("DELETE", scoreData, userEmail);
  },

  // Get audit logs for a specific team
  async getTeamAuditLogs(teamId) {
    try {
      const auditQuery = query(
        collection(getDb(), AUDIT_COLLECTION),
        where("teamId", "==", parseInt(teamId)),
        orderBy("timestamp", "desc")
      );

      const querySnapshot = await getDocs(auditQuery);
      const logs = [];

      querySnapshot.forEach((doc) => {
        logs.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return logs;
    } catch (error) {
      console.error("Error getting team audit logs:", error);
      throw error;
    }
  },

  // Get audit logs for a specific hole/round
  async getHoleAuditLogs(teamId, hole, round) {
    try {
      const auditQuery = query(
        collection(getDb(), AUDIT_COLLECTION),
        where("teamId", "==", parseInt(teamId)),
        where("hole", "==", parseInt(hole)),
        where("round", "==", parseInt(round)),
        orderBy("timestamp", "desc")
      );

      const querySnapshot = await getDocs(auditQuery);
      const logs = [];

      querySnapshot.forEach((doc) => {
        logs.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return logs;
    } catch (error) {
      console.error("Error getting hole audit logs:", error);
      throw error;
    }
  },

  // Get all audit logs (admin function)
  async getAllAuditLogs(limit = 100) {
    try {
      const auditQuery = query(
        collection(getDb(), AUDIT_COLLECTION),
        orderBy("timestamp", "desc")
      );

      const querySnapshot = await getDocs(auditQuery);
      const logs = [];

      querySnapshot.forEach((doc) => {
        logs.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return logs.slice(0, limit);
    } catch (error) {
      console.error("Error getting all audit logs:", error);
      throw error;
    }
  },

  // Real-time listener for audit logs (admin monitoring)
  onAuditLogsChange(callback, teamId = null) {
    let auditQuery;

    if (teamId) {
      auditQuery = query(
        collection(getDb(), AUDIT_COLLECTION),
        where("teamId", "==", parseInt(teamId)),
        orderBy("timestamp", "desc")
      );
    } else {
      auditQuery = query(
        collection(getDb(), AUDIT_COLLECTION),
        orderBy("timestamp", "desc")
      );
    }

    return onSnapshot(auditQuery, (querySnapshot) => {
      const logs = [];
      querySnapshot.forEach((doc) => {
        logs.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      callback(logs);
    });
  },

  // Generate audit report
  generateAuditReport(logs) {
    const report = {
      totalChanges: logs.length,
      changesByAction: {},
      changesByTeam: {},
      changesByUser: {},
      suspiciousActivity: [],
      timeline: [],
    };

    logs.forEach((log) => {
      // Count by action
      report.changesByAction[log.action] =
        (report.changesByAction[log.action] || 0) + 1;

      // Count by team
      report.changesByTeam[log.teamId] =
        (report.changesByTeam[log.teamId] || 0) + 1;

      // Count by user
      report.changesByUser[log.userEmail] =
        (report.changesByUser[log.userEmail] || 0) + 1;

      // Check for suspicious activity
      if (log.action === "UPDATE" && log.oldScore && log.newScore) {
        const scoreDiff = Math.abs(log.newScore - log.oldScore);
        if (scoreDiff > 3) {
          report.suspiciousActivity.push({
            ...log,
            reason: `Large score change: ${log.oldScore} → ${log.newScore} (diff: ${scoreDiff})`,
          });
        }
      }

      // Add to timeline
      report.timeline.push({
        timestamp: log.timestamp,
        summary: `${
          log.userEmail
        } ${log.action.toLowerCase()}d score for Team ${log.teamId}, Hole ${
          log.hole
        }, Round ${log.round}: ${log.newScore}`,
      });
    });

    return report;
  },
};
